/**
 * Investor Query Hooks
 *
 * Phase 116.6 — reads `score_event` (post-cutover source of truth) to enrich
 * dealflow with the latest ScoreEventReadModel. Replaces the prior
 * `score_results`/`aiScoreResult` plumbing.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from './client';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { queryKeys } from './api';
import { fetchInvestorScoreEvents } from './investor-score-events';
import { useTrackRecord } from './track-record';
import type { Database } from './database.types';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import type { DealflowStartup, PortfolioCompany, InvestorCommitment, ExternalCommitment } from '@/lib/state/dealflow-store';
import type { AuthUserId } from '@/lib/ids';
import type { StartupSummary, ScoreEventReadModel } from './edge-functions';
import type { DealStage } from '@/lib/dealflow/stage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Raw dealflow row joined with startup_profiles via Supabase select */
type DealflowDbRow = Database['public']['Tables']['dealflow']['Row'];
type StartupProfileRow = Database['public']['Tables']['startup_profiles']['Row'];

/**
 * A startup_profiles row with the paired directory row embedded for its logo.
 *
 * PostgREST returns an embedded reverse relation as an ARRAY unless it can
 * prove the relation is to-one. `company_directory_startup_profile_uniq` is a
 * PARTIAL unique index, which PostgREST does not treat as proof, so this is
 * typed and read as an array and the first element taken. It has at most one
 * element — that is exactly what the index guarantees.
 */
type StartupProfileWithDirectory = StartupProfileRow & {
  company_directory?: { logo_url: string | null; logo_is_knockout?: boolean | null }[] | null;
};

/** The logo of the directory row paired with this profile, if any. */
function embeddedLogoUrl(sp: StartupProfileWithDirectory | null): string | null {
  return sp?.company_directory?.[0]?.logo_url ?? null;
}

/**
 * Whether the joined logo is a near-white knockout needing a dark chip.
 *
 * Carried alongside the URL because it is a property OF that logo — a surface
 * that has one without the other renders a white wordmark on a light card and
 * shows nothing at all.
 */
function embeddedLogoIsKnockout(sp: StartupProfileWithDirectory | null): boolean {
  return sp?.company_directory?.[0]?.logo_is_knockout === true;
}

interface DealflowRowWithStartup extends DealflowDbRow {
  startup: StartupProfileWithDirectory | null;
}

// ---------------------------------------------------------------------------
// Transformer
// ---------------------------------------------------------------------------

/**
 * Build a StartupSummary from a raw startup_profiles row.
 *
 * Shared by the dealflow transformer and the raising-startups feed so both
 * surfaces render identical company/team/traction data from the same
 * (RLS-visible) profile columns.
 */
export function startupProfileToStartupSummary(
  sp: StartupProfileWithDirectory | null,
  fallbackId: string,
): StartupSummary {
  return {
    id: sp?.id ?? fallbackId,
    company: {
      name: sp?.name ?? 'Unknown',
      tagline: sp?.tagline ?? '',
      description: '',
      industry: sp?.industry ?? '',
      stage: sp?.stage ?? '',
      location: sp?.location ?? '',
      website: sp?.website ?? '',
      founded: sp?.founded_year ?? 0,
      // Joined from company_directory rather than stored on the profile — see
      // StartupSummary.company.logoUrl. Without this, dealflow cards fell back
      // to initials even though the logo was extracted, re-hosted and served:
      // `logo_url` was only ever read by company-directory.tsx and by
      // startup-detail.tsx when source === 'directory'.
      logoUrl: embeddedLogoUrl(sp),
      logoIsKnockout: embeddedLogoIsKnockout(sp),
    },
    team: {
      people: [],
      size: sp?.team_size ?? 0,
      compositionSummary: '',
    },
    problemSolution: {
      problem: sp?.problem ?? '',
      solution: sp?.solution ?? '',
    },
    market: {
      tam: sp?.market_size ?? 0,
      sam: 0,
      som: 0,
      tamSource: '',
      growthRate: 0,
      competitors: (sp?.competitors ?? []).map((c) => ({
        name: c,
        relationship: 'competitor',
      })),
    },
    traction: {
      metrics: [
        sp?.monthly_revenue != null
          ? { name: 'MRR', value: sp.monthly_revenue, unit: 'USD' }
          : null,
        sp?.user_count != null
          ? { name: 'Users', value: sp.user_count, unit: 'count' }
          : null,
        sp?.growth_rate != null
          ? { name: 'Growth', value: sp.growth_rate, unit: '%' }
          : null,
      ].filter((m): m is { name: string; value: number; unit: string } => m !== null),
    },
    financials: {
      unitEconomics: {},
      projections: [],
    },
    funding: {
      currentRaise: sp?.funding_target
        ? { amount: sp.funding_target, currency: 'USD' }
        : null,
      history: [],
      isActivelyRaising: sp?.is_actively_raising ?? false,
    },
    product: [],
    gtm: {
      channels: [],
      businessModel: '',
      pricingModel: '',
      distributionSummary: '',
    },
    socialProof: {
      investorNames: [],
      partnerships: [],
      awards: [],
    },
    deckQuality: {
      structureScore: 0,
      narrativeFlow: '',
      designQuality: '',
      completeness: 0,
      missingSections: [],
    },
  };
}

/**
 * Transform a raw dealflow DB row (joined with startup_profiles) into a
 * DealflowStartup shape expected by the store and UI components.
 *
 * Phase 116.6: enrichment now comes from a ScoreEventReadModel built from
 * `score_event` (the post-cutover source of truth).
 */
export function dealflowRowToDealflowStartup(
  row: DealflowRowWithStartup,
  scoreEvent?: ScoreEventReadModel | null,
): DealflowStartup {
  return {
    id: row.id,
    startup: startupProfileToStartupSummary(row.startup, row.startup_id),
    // null, NOT 0 — see DealflowStartup.readinessScore. A missing score event
    // means "not scored", and 0 reads as 'critical'.
    readinessScore: scoreEvent?.composite.finalScore ?? null,
    lastActivity: row.updated_at ?? '',
    status: row.status ?? 'screening',
    source: 'pipeline',
    // A NULL embed: usually a sealed company (119-07). Confirmed by the card RPC on screen.
    ...(row.startup === null ? { profileMissing: true } : {}),
    notes: row.notes ?? undefined,
    scoreEvent: scoreEvent ?? undefined,
  };
}

/**
 * Transform an actively-raising startup_profiles row (no dealflow row exists)
 * into the same DealflowStartup shape the dealflow UI renders.
 *
 * The item `id` is the startup_profiles id — startup-detail and
 * investor-score-detail resolve feed items by that id. `source: 'raising'`
 * lets screens distinguish feed items from real pipeline entries.
 */
export function raisingStartupToDealflowStartup(
  sp: StartupProfileWithDirectory,
  scoreEvent?: ScoreEventReadModel | null,
): DealflowStartup {
  return {
    id: sp.id,
    startup: startupProfileToStartupSummary(sp, sp.id),
    // null, NOT 0 — see DealflowStartup.readinessScore. A missing score event
    // means "not scored", and 0 reads as 'critical'.
    readinessScore: scoreEvent?.composite.finalScore ?? null,
    lastActivity: sp.updated_at ?? '',
    status: 'screening',
    source: 'raising',
    scoreEvent: scoreEvent ?? undefined,
  };
}

/** Raw score_event row shape used by the read-model mapper (table not in generated types). */
export interface ScoreEventRow {
  id: string;
  startup_id: string;
  computed_at: string;
  methodology_version: string;
  composite: ScoreEventReadModel['composite'];
  dimensions: ScoreEventReadModel['dimensions'] | null;
  risks: ScoreEventReadModel['risks'] | null;
  weights_used: { stage?: string | null } | null;
  /** investor_score_events only: false when proof material was withheld. */
  raw_inputs_visible?: boolean;
}

/**
 * Latest projected score event per startup (investor_score_events, latest-only).
 * Never a direct score_event table read: investors have no SELECT policy on the raw row
 * (it holds the founder's raw inputs) — see investor-score-events.ts.
 * A failure is reported, not thrown, so the pipeline still renders unscored.
 */
async function fetchLatestScoreEventRows(startupIds: string[], context: string): Promise<ScoreEventRow[]> {
  try {
    return await fetchInvestorScoreEvents(startupIds, { latestOnly: true });
  } catch (scoreError) {
    reportError(scoreError, { context, silent: true });
    return [];
  }
}

/**
 * Build a map of startup_id -> latest ScoreEventReadModel from score_event
 * rows that are already ordered by computed_at DESC (first occurrence wins).
 */
export function buildScoreEventMap(
  eventRows: ScoreEventRow[],
  getProfile: (startupId: string) => StartupProfileRow | null | undefined,
): Map<string, ScoreEventReadModel> {
  const scoreMap = new Map<string, ScoreEventReadModel>();
  for (const e of eventRows) {
    if (scoreMap.has(e.startup_id)) continue; // first occurrence is latest (ordered desc)
    const sp = getProfile(e.startup_id);
    scoreMap.set(e.startup_id, {
      scoreEventId: e.id,
      methodologyVersion: e.methodology_version,
      computedAt: e.computed_at,
      composite: e.composite,
      dimensions: e.dimensions ?? [],
      risks: e.risks ?? [],
      ...(e.raw_inputs_visible !== undefined ? { rawInputsVisible: e.raw_inputs_visible } : {}),
      startup: {
        id: e.startup_id,
        name: sp?.name ?? '',
        stage: e.weights_used?.stage ?? sp?.stage ?? null,
        sector: sp?.industry ?? null,
      },
    });
  }
  return scoreMap;
}

/**
 * Merge server dealflow rows into the current store contents.
 *
 * THE SERVER IS THE PIPELINE. Whatever the `dealflow` table returns is the
 * whole pipeline; nothing local survives it.
 *
 * This used to preserve locally analyzed items (`deal-*` ids) that the server
 * did not know about. That was necessary while an analysed deck had no server
 * identity — the extraction minted a synthetic `ss-<ts>` id, so there was
 * nothing a dealflow row could reference, and dropping the local card meant
 * losing the analysis. The cost was orphans that could never sync: in
 * production on 2026-07-30 the web dealflow showed 8 deals against 5 server
 * rows, the extra three living only in one browser's storage and invisible on
 * every other device.
 *
 * `investor-analyze-deck` now creates the dealflow row itself, so that
 * workaround is obsolete AND harmful: a local item with no server counterpart
 * now means the write failed, and preserving it hides the failure behind a card
 * that looks fine — the same silent-success shape as the rest of this codebase's
 * recurring bugs. Let it disappear on refetch so the gap is visible.
 *
 * Optimistic local items are still fine BETWEEN the analysis and the next
 * refetch; they simply do not outlive it.
 */
export function mergeServerDealflow(
  serverItems: DealflowStartup[],
  _currentItems: DealflowStartup[],
): DealflowStartup[] {
  return [...serverItems];
}

// ---------------------------------------------------------------------------
// Enhanced Hook
// ---------------------------------------------------------------------------

/**
 * Fetch dealflow items from Supabase and transform them into DealflowStartup[].
 *
 * - Queries the `dealflow` table joined with `startup_profiles`
 * - Batch-fetches the latest `score_results` for each startup in the dealflow
 * - Transforms each row via `dealflowRowToDealflowStartup`
 *
 * Returns data ready to be passed to `useDealflowStore.getState().setDealflow()`.
 *
 * Only enabled in production mode (when `!mode.showSampleData`).
 */
export function useDealflowFromSupabase(investorId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.dealflow(investorId ?? ''), 'enriched'] as const,
    queryFn: async (): Promise<DealflowStartup[]> => {
      if (!investorId || !isSupabaseConfigured()) return [];

      // 1. Fetch dealflow rows with joined startup profiles
      const { data: dealflowRows, error: dealflowError } = await supabase
        .from('dealflow')
        .select(`
          *,
          startup:startup_profiles(*, company_directory(logo_url, logo_is_knockout))
        `)
        .eq('investor_profile_id', investorId)
        .order('updated_at', { ascending: false });

      if (dealflowError) throw dealflowError;
      if (!dealflowRows || dealflowRows.length === 0) return [];

      // 2. Latest projected score event for each startup in dealflow.
      const startupIds = dealflowRows.map((r) => r.startup_id);
      const eventRows = await fetchLatestScoreEventRows(startupIds, 'useDealflowFromSupabase.scoreFetch');

      // Build a map: startup_id -> latest ScoreEventReadModel
      const rows = dealflowRows as unknown as DealflowRowWithStartup[];
      const scoreMap = buildScoreEventMap(
        eventRows,
        (startupId) => rows.find((r) => r.startup_id === startupId)?.startup,
      );

      // 3. Transform each dealflow row
      return rows.map((row) =>
        dealflowRowToDealflowStartup(row, scoreMap.get(row.startup_id) ?? null),
      );
    },
    enabled: !!investorId && isSupabaseConfigured() && !mode.showSampleData,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ---------------------------------------------------------------------------
// Raising-Startups Feed
// ---------------------------------------------------------------------------

export const raisingStartupsQueryKey = ['raising-startups', 'enriched'] as const;

/**
 * Fetch all actively-raising startups with their latest score event — the
 * platform-wide feed shown to every investor in the default dealflow
 * ("Raising Now") and on the Discover screen.
 *
 * Reads ONLY startup_profiles (RLS: investors read actively-raising rows)
 * and the investor_score_events projection (actively-raising route).
 * No data-room, claims, team-intelligence, or interview data is touched.
 *
 * Only enabled in production mode (when `!mode.showSampleData`).
 */
export function useRaisingStartups() {
  return useQuery({
    queryKey: raisingStartupsQueryKey,
    queryFn: async (): Promise<DealflowStartup[]> => {
      if (!isSupabaseConfigured()) return [];

      // Sealed companies (closed / acquired / bankrupt, 119-02) never appear
      // here, with no filter needed: `startup_profiles` carries a RESTRICTIVE
      // `company_seal` policy for every client role, and the close itself sets
      // `is_actively_raising = false` (set_startup_lifecycle, 119-01). Do not
      // move this feed to a SECURITY DEFINER read without re-adding the seal.
      const { data: profileRows, error: profileError } = await supabase
        .from('startup_profiles')
        .select('*, company_directory(logo_url, logo_is_knockout)')
        .eq('is_actively_raising', true)
        .order('updated_at', { ascending: false });

      if (profileError) throw profileError;
      if (!profileRows || profileRows.length === 0) return [];

      const profiles = profileRows as StartupProfileRow[];
      const profilesById = new Map(profiles.map((sp) => [sp.id, sp]));

      const eventRows = await fetchLatestScoreEventRows(
        profiles.map((sp) => sp.id),
        'useRaisingStartups.scoreFetch',
      );
      const scoreMap = buildScoreEventMap(
        eventRows,
        (startupId) => profilesById.get(startupId),
      );

      return profiles.map((sp) =>
        raisingStartupToDealflowStartup(sp, scoreMap.get(sp.id) ?? null),
      );
    },
    enabled: isSupabaseConfigured() && !mode.showSampleData,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Add a raising-feed startup to the investor's pipeline as a real `dealflow`
 * row — 'interested' or 'rejected' triage it, 'monitoring' follows it without
 * triaging. Upsert keyed on the (investor_user_id, startup_id) unique
 * constraint so repeat taps are idempotent.
 *
 * A raising-feed item has no dealflow row yet, so creating one is equivalent
 * to a deal arriving at `screening` and being acted on immediately — the
 * legal targets are exactly `screening`'s transitions (see
 * `canTransition('screening', status)` at the two call sites). Deliberately
 * NOT the full `DealStage`: `screening`/`diligence`/`invested` are not things
 * the raising feed can create, and a wider type would let a caller pass one
 * of them and only fail at the DB, not at compile time.
 *
 * On success the new pipeline item is pushed into the dealflow store
 * immediately and the enriched dealflow query is invalidated so the layout
 * sync refetches canonical data.
 */
export function useAddRaisingStartupToDealflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      /** investor_profiles.id (NOT the auth user id — dealflow FK). */
      investorProfileId: string;
      /** The raising-feed item (id = startup_profiles id). */
      feedItem: DealflowStartup;
      status: Extract<DealStage, 'interested' | 'rejected' | 'monitoring'>;
    }) => {
      const { investorProfileId, feedItem, status } = params;
      const { data, error } = await supabase
        .from('dealflow')
        .upsert(
          {
            investor_profile_id: investorProfileId,
            startup_id: feedItem.startup.id,
            status,
          },
          { onConflict: 'investor_profile_id,startup_id' },
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (row, { feedItem, investorProfileId, status }) => {
      // Immediate store update: the item leaves "Raising Now" and appears in
      // the pipeline without waiting for the refetch.
      const store = useDealflowStore.getState();
      const alreadyInPipeline = store.dealflow.some(
        (d) => d.id === row.id || d.startup.id === feedItem.startup.id,
      );
      if (!alreadyInPipeline) {
        store.addDealflowItem({
          ...feedItem,
          id: row.id,
          status,
          source: 'pipeline',
          lastActivity: row.updated_at ?? new Date().toISOString(),
        });
      } else {
        store.updateDealflowStatus(row.id, status);
      }
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.dealflow(investorProfileId), 'enriched'],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Portfolio Hook
// ---------------------------------------------------------------------------

/** Raw portfolio_companies row shape (matches DB schema) */
interface PortfolioDbRow {
  id: string;
  investor_profile_id: string;
  startup_id: string;
  startup_name: string;
  investment_date: string;
  investment_amount: number;
  equity: number | null;
  current_valuation: number | null;
  status: string;
  returns: number | null;
  updates: unknown; // JSONB
  created_at: string;
  updated_at: string;
}

/**
 * Transform a portfolio DB row into a PortfolioCompany shape for the store.
 */
function portfolioRowToPortfolioCompany(row: PortfolioDbRow): PortfolioCompany {
  const startup: StartupSummary = {
    id: row.startup_id,
    company: {
      name: row.startup_name,
      tagline: '',
      description: '',
      industry: '',
      stage: '',
      location: '',
      website: '',
      founded: 0,
    },
    team: { people: [], size: 0, compositionSummary: '' },
    problemSolution: { problem: '', solution: '' },
    market: { tam: 0, sam: 0, som: 0, tamSource: '', growthRate: 0, competitors: [] },
    traction: { metrics: [] },
    financials: { unitEconomics: {}, projections: [] },
    funding: { currentRaise: null, history: [], isActivelyRaising: false },
    product: [],
    gtm: { channels: [], businessModel: '', pricingModel: '', distributionSummary: '' },
    socialProof: { investorNames: [], partnerships: [], awards: [] },
    deckQuality: { structureScore: 0, narrativeFlow: '', designQuality: '', completeness: 0, missingSections: [] },
  };

  const updates = Array.isArray(row.updates) ? (row.updates as PortfolioCompany['updates']) : [];

  return {
    id: row.id,
    startup,
    investmentDate: row.investment_date,
    investmentAmount: row.investment_amount,
    equity: row.equity ?? 0,
    currentValuation: row.current_valuation ?? 0,
    status: row.status as PortfolioCompany['status'],
    returns: row.returns ?? undefined,
    updates,
  };
}

/**
 * Fetch portfolio companies for the signed-in investor.
 *
 * TAKES THE AUTH USER ID — NOT `investor_profiles.id`.
 *
 * `portfolio_companies.investor_profile_id` has a FK to `auth.users(id)` and every RLS
 * policy on the table is `investor_profile_id = auth.uid()`. Passing the investor
 * PROFILE id (as the callers did until 2026-07-30) matches zero rows on read
 * and is rejected by the INSERT `WITH CHECK` on write — silently, because the
 * caller never awaited the mutation. The parameter is named `userId` so the
 * next reader is not invited to hand it a profile id.
 *
 * Note the contrast with `dealflow.investor_profile_id`, which IS an
 * investor_profiles.id. The two id spaces sit side by side in this file; that
 * is precisely why this comment exists.
 */
export function usePortfolioFromSupabase(userId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.portfolio(userId ?? ''), 'enriched'] as const,
    queryFn: async (): Promise<PortfolioCompany[]> => {
      // Sample mode is served from the SAME hook rather than from a parallel
      // store slice. `useDealflowStore.portfolio` used to hold the demo
      // portfolio, seeded at demo login and read directly by some screens —
      // which meant those screens read a store that is ALWAYS EMPTY in
      // production (the assistant and startup-detail both did, silently). One
      // source, branching internally, is what stops the two from disagreeing.
      if (mode.showSampleData) {
        const { getMockPortfolio } = await import('@/lib/state/mock-data-provider');
        return getMockPortfolio();
      }

      if (!userId || !isSupabaseConfigured()) return [];

      // Cast required: portfolio_companies table not yet in database.types.ts
      // (migration 20260323000002 creates table, types will be regenerated)
      const { data, error } = await (supabase as any)
        .from('portfolio_companies')
        .select('*')
        .eq('investor_user_id', userId)
        .order('investment_date', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      return (data as unknown as PortfolioDbRow[]).map(portfolioRowToPortfolioCompany);
    },
    enabled: mode.showSampleData || (!!userId && isSupabaseConfigured()),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Everything the investor would call "my portfolio", from ONE definition.
 *
 * The Portfolio screen lists holdings AND track record; the dashboard tile was
 * counting holdings only. So an investor with eight declared past investments
 * and no paid holdings saw "0 active companies" on the dashboard and eight on
 * the Portfolio screen — two screens disagreeing about the same word, which is
 * exactly the defect the portfolio work set out to end.
 *
 * The money metrics are deliberately NOT widened to match. Track-record entries
 * carry no cheque size — onboarding never asks — so including them would divide
 * a real total by an inflated count and report an invested figure nobody
 * invested. Count covers both; economics cover holdings.
 */
export function useInvestorPortfolioEntries(userId: AuthUserId | undefined) {
  const holdingsQuery = usePortfolioFromSupabase(userId);
  const trackRecordQuery = useTrackRecord(userId);

  const holdings = holdingsQuery.data ?? [];
  const trackRecord = trackRecordQuery.data ?? [];

  return {
    holdings,
    trackRecord,
    /** What "N companies" means on any surface. */
    totalCompanies: holdings.length + trackRecord.length,
    isLoading: holdingsQuery.isLoading || trackRecordQuery.isLoading,
  };
}

// ---------------------------------------------------------------------------
// Commitments Hook
// ---------------------------------------------------------------------------

/** Raw commitments row shape (matches DB schema) */
interface CommitmentDbRow {
  id: string;
  investor_user_id: string;
  investor_name: string;
  investor_firm: string | null;
  startup_id: string;
  commitment_type: string;
  source: string;
  amount: number;
  previous_amount: number | null;
  status: string;
  notes: string | null;
  conclusion_id: string | null;
  converted_at: string | null;
  withdrawn_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch commitments from Supabase for a given investor, split by source.
 *
 * Returns `{ commitments, externalCommitments }` ready for the store.
 * Only enabled in production mode.
 */
export function useCommitmentsFromSupabase(investorId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.commitments(investorId ?? ''), 'split'] as const,
    queryFn: async (): Promise<{
      commitments: InvestorCommitment[];
      externalCommitments: ExternalCommitment[];
    }> => {
      if (!investorId || !isSupabaseConfigured()) {
        return { commitments: [], externalCommitments: [] };
      }

      // Cast required: commitments table not yet in database.types.ts
      // (migration 20260323000002 creates table, types will be regenerated)
      const { data, error } = await (supabase as any)
        .from('commitments')
        .select('*')
        .eq('investor_user_id', investorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) {
        return { commitments: [], externalCommitments: [] };
      }

      const rows = data as unknown as CommitmentDbRow[];

      const verified: InvestorCommitment[] = [];
      const unverified: ExternalCommitment[] = [];

      for (const row of rows) {
        if (row.source === 'verified') {
          verified.push({
            id: row.id,
            investorId: row.investor_user_id,
            investorName: row.investor_name,
            investorFirm: row.investor_firm ?? undefined,
            startupId: row.startup_id,
            type: row.commitment_type as InvestorCommitment['type'],
            source: 'verified',
            amount: row.amount,
            previousAmount: row.previous_amount ?? undefined,
            status: row.status as InvestorCommitment['status'],
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            convertedAt: row.converted_at ?? undefined,
            withdrawnAt: row.withdrawn_at ?? undefined,
            notes: row.notes ?? undefined,
            conclusionId: row.conclusion_id ?? undefined,
          });
        } else {
          unverified.push({
            id: row.id,
            startupId: row.startup_id,
            investorName: row.investor_name,
            investorFirm: row.investor_firm ?? undefined,
            type: row.commitment_type as ExternalCommitment['type'],
            source: 'unverified',
            amount: row.amount,
            status: row.status as ExternalCommitment['status'],
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            notes: row.notes ?? undefined,
          });
        }
      }

      return { commitments: verified, externalCommitments: unverified };
    },
    enabled: !!investorId && isSupabaseConfigured() && !mode.showSampleData,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ---------------------------------------------------------------------------
// Completed DD Reviews Hook
// ---------------------------------------------------------------------------

export interface CompletedDD {
  id: string;
  startupId: string;
  startupName: string;
  industry: string;
  stage: string;
  /**
   * The dd_conclusion_decision enum is now `invest | pass | monitor`. The
   * legacy spellings stay in the union because concluded rows written before
   * the enum was collapsed still carry them, and `undecided` is the read
   * fallback for a row whose decision is NULL — not a value anything writes.
   */
  decision: 'invest' | 'pass' | 'monitor' | 'follow' | 'undecided' | 'follow_up';
  amount: number | null;
  notes: string | null;
  completedAt: string | null;
  startDate: string | null;
  // Optional rich fields populated by mock data and (eventually) the LLM-augmented
  // server response. Real Supabase rows leave these undefined; UI uses optional access.
  endDate?: string;
  durationDays?: number;
  dataRoomVersion?: string;
  documentsReviewed?: number;
  flaggedIssues?: number;
  finalComments?: string;
  executiveSummary?: string;
  investmentThesis?: string;
  keyRisks?: string;
  notablePoints?: { type: 'strength' | 'concern' | 'neutral'; text: string }[];
  /** Narrative review state (Phase C.2): none = not generated, draft = LLM draft, edited = investor-edited. */
  narrativeStatus?: 'none' | 'draft' | 'edited';
  /** Phase D: shared with the startup (opt-in). */
  sharedWithStartup?: boolean;
  sharedAt?: string;
  /** Real, provenance-tagged objective metrics (Phase C.1). See `@/lib/dd/dd-metrics`. */
  metrics?: import('@/lib/dd/dd-metrics').DDMetric[];
  scoresAtStart?: { overall: number; confidence: number; risk: number };
  scoresAtEnd?: { overall: number; confidence: number; risk: number };
}

/**
 * Fetch completed DD reviews for an investor.
 * Queries dd_collaborators where conclusion_decision is not null,
 * joined with shared_dd for startup context.
 */
export function useCompletedDDs(investorId: string | undefined) {
  return useQuery({
    queryKey: ['completed-dds', investorId],
    queryFn: async (): Promise<CompletedDD[]> => {
      if (!investorId || !isSupabaseConfigured()) return [];

      // JUSTIFIED as any: dd_collaborators/shared_dd not in generated types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('dd_collaborators')
        .select(`
          id,
          startup_id,
          conclusion_decision,
          conclusion_amount,
          conclusion_notes,
          conclusion_updated_at,
          joined_at,
          shared_dd!inner (
            startup_name,
            startup_industry,
            startup_stage
          )
        `)
        .eq('investor_user_id', investorId)
        .not('conclusion_decision', 'is', null)
        .order('conclusion_updated_at', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any[]).map((row) => ({
        id: row.id,
        startupId: row.startup_id,
        startupName: row.shared_dd?.startup_name ?? 'Unknown',
        industry: row.shared_dd?.startup_industry ?? '',
        stage: row.shared_dd?.startup_stage ?? '',
        decision: row.conclusion_decision,
        amount: row.conclusion_amount,
        notes: row.conclusion_notes,
        completedAt: row.conclusion_updated_at,
        startDate: row.joined_at,
      }));
    },
    enabled: !!investorId && isSupabaseConfigured(),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Portfolio Upsert Mutation
// ---------------------------------------------------------------------------

/**
 * Add or update a portfolio company in the database.
 */
export function useUpsertPortfolioCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      /** auth.users id (auth.uid()) — NOT investor_profiles.id. */
      userId: string;
      company: {
        startup_name: string;
        investment_date?: string;
        investment_amount?: number;
        equity?: number;
        current_valuation?: number;
        status?: string;
        id?: string;
      };
    }) => {
      const { userId, company } = params;
      const { id, ...fields } = company;
      // auth.users id — see usePortfolioFromSupabase. The RLS WITH CHECK is
      // `investor_user_id = auth.uid()`, so a profile id here is rejected outright.
      const row = { investor_user_id: userId, ...fields };

      if (id) {
        // JUSTIFIED as any: portfolio_companies not in generated types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from('portfolio_companies')
          .update(row)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('portfolio_companies')
        .insert(row)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.portfolio(vars.userId), 'enriched'] });
    },
  });
}

/**
 * Delete one holding.
 *
 * Added because the Portfolio screen's delete removed the row from a local
 * store and never touched the database — so a deleted holding reappeared on
 * the next fetch, and on any other device it had never gone. Same
 * silent-no-op family as the fire-and-forget save fixed on 2026-07-30.
 *
 * The `investor_user_id` filter is belt-and-braces: RLS already restricts DELETE to
 * `investor_user_id = auth.uid()`, but stating it here means a wrong id fails
 * visibly in development rather than silently matching nothing.
 */
export function useDeletePortfolioCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      /** auth.users id (auth.uid()) — NOT investor_profiles.id. */
      userId: string;
      id: string;
    }) => {
      // JUSTIFIED as any: portfolio_companies not in generated types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('portfolio_companies')
        .delete()
        .eq('id', params.id)
        .eq('investor_user_id', params.userId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.portfolio(vars.userId), 'enriched'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Investor Profile Update Mutation
// ---------------------------------------------------------------------------

/**
 * Update investor profile fields (bio, value_add, firm, location, etc.)
 */
export function useUpdateInvestorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (params: {
      investorId: string;
      updates: Record<string, any>;
    }) => {
      const { investorId, updates } = params;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('investor_profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', investorId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investor-profile'] });
    },
  });
}
